/**
 * 路由配置
 * 采用动态路由模式，业务路由从数据库加载
 *
 * @author system
 * @version 2.0.0 - 完全动态路由版本
 */

import { createRouter, createWebHistory } from 'vue-router'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import Layout from '@/layouts/index.vue'
import { useUserStore } from '@/stores/user'
import { usePermissionStore } from '@/stores/permission'
import i18n from '@/locales'

// NProgress 配置
NProgress.configure({ showSpinner: false })

/**
 * 静态路由
 * 这些路由不需要权限验证，在应用启动时就注册
 */
export const constantRoutes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录', hidden: true }
  },
  {
    path: '/dashboard',
    component: Layout,
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '首页', icon: 'House', affix: true }
      }
    ]
  },
  {
    path: '/',
    redirect: '/dashboard',
    meta: { hidden: true }
  },
  {
    path: '/merchant/policy/:parentId/assets',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'AssetPolicyPage',
        component: () => import('@/views/merchant/policy/assets.vue'),
        meta: { title: '资源定价', hidden: true }
      }
    ]
  },
  {
    path: '/profile',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'Profile',
        component: () => import('@/views/profile/index.vue'),
        meta: { title: '个人中心' }
      }
    ]
  },
  // 注意：/contract 路由已由动态路由系统（RouteBuilder）生成，不再需要静态定义
  // /contract/amend/:contractId 是独立页面，不需要 /contract 父路由即可工作
  // 合同变更页面
  {
    path: '/contract/amend/:contractId',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'ContractAmend',
        component: () => import('@/views/merchant/contract/AmendPage.vue'),
        meta: { title: '合同变更', hidden: true }
      }
    ]
  },
  // 合同历史全量只读快照
  {
    path: '/contract/history/:historyId',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'ContractHistoryView',
        component: () => import('@/views/merchant/contract/HistoryView.vue'),
        meta: { title: '合同历史快照', hidden: true }
      }
    ]
  },
  // ==================== 工作流通知统一跳转路由 ====================
  // 所有工作流通知使用稳定的业务路径（/业务/detail/{id}），
  // 直接加载组件，不依赖动态路由是否注册，确保任何有通知的用户都能访问。
  // beforeEnter 将 :id 参数转为 ?id= 查询参数，兼容页面组件的读取方式。
  // 新接入工作流的业务必须在此注册。

  // 账单详情
  {
    path: '/bill/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/payment/bill/detail.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          next({ path: to.path, query: { ...to.query, id: to.params.id } })
        } else { next() }
      },
      meta: { title: '账单详情', hidden: true }
    }]
  },
  // 租赁合同详情（支持 tab 参数，如终止审批跳转到终止 tab）
  {
    path: '/contract/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/merchant/contract/index.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          const query = { ...to.query, id: to.params.id }
          if (to.query.tab) query.tab = to.query.tab
          next({ path: to.path, query })
        } else { next() }
      },
      meta: { title: '合同详情', hidden: true }
    }]
  },
  // 联营合同详情
  {
    path: '/joint-contract/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/joint/contract/index.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          next({ path: to.path, query: { ...to.query, id: to.params.id } })
        } else { next() }
      },
      meta: { title: '联营合同详情', hidden: true }
    }]
  },
  // 联营结算详情
  {
    path: '/settlement/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/joint/settlement/index.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          next({ path: to.path, query: { ...to.query, id: to.params.id } })
        } else { next() }
      },
      meta: { title: '联营结算详情', hidden: true }
    }]
  },
  // 采购合同详情
  {
    path: '/purchase-contract/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/self/purchase/index.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          next({ path: to.path, query: { ...to.query, id: to.params.id } })
        } else { next() }
      },
      meta: { title: '采购合同详情', hidden: true }
    }]
  },
  // 意向详情
  {
    path: '/intention/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/merchant/intention/index.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          next({ path: to.path, query: { ...to.query, id: to.params.id } })
        } else { next() }
      },
      meta: { title: '意向详情', hidden: true }
    }]
  },
  // 装修详情
  {
    path: '/renovation/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/operation/renovation/index.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          next({ path: to.path, query: { ...to.query, id: to.params.id } })
        } else { next() }
      },
      meta: { title: '装修详情', hidden: true }
    }]
  },
  // 退出详情
  {
    path: '/termination/detail/:id',
    component: Layout,
    meta: { hidden: true },
    children: [{
      path: '',
      component: () => import('@/views/operation/termination/index.vue'),
      beforeEnter: (to, from, next) => {
        if (to.params.id && !to.query.id) {
          next({ path: to.path, query: { ...to.query, id: to.params.id } })
        } else { next() }
      },
      meta: { title: '退出详情', hidden: true }
    }]
  },
  // 收款登记页面（列表+新增）
  {
    path: '/payment/register',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'PaymentRegister',
        component: () => import('@/views/payment/register/index.vue'),
        meta: { title: '收款登记', hidden: true }
      },
      {
        path: 'add',
        name: 'PaymentRegisterAdd',
        component: () => import('@/views/payment/register/AddPage.vue'),
        meta: { title: '新增收款', hidden: true }
      },
      {
        path: 'edit/:id',
        name: 'PaymentRegisterEdit',
        component: () => import('@/views/payment/register/AddPage.vue'),
        meta: { title: '编辑收款', hidden: true }
      }
    ]
  },
  {
    path: '/system/printTemplate/designer',
    name: 'PrintTemplateDesigner',
    component: () => import('@/views/system/printTemplate/designer.vue'),
    meta: { title: '模板设计器', hidden: true }
  },
  {
    path: '/redirect',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: '/redirect/:path(.*)',
        component: () => import('@/views/redirect/index.vue'),
        meta: { hidden: true }
      }
    ]
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '404', hidden: true }
  },
  {
    path: '/401',
    name: 'Unauthorized',
    component: () => import('@/views/error/401.vue'),
    meta: { title: '401', hidden: true }
  }
]

/**
 * 创建路由实例
 * 只包含静态路由，动态路由在登录后通过 addRoute 添加
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: constantRoutes,
  scrollBehavior: () => ({ left: 0, top: 0 })
})

/**
 * 白名单路由 - 无需登录即可访问
 */
const whiteList = ['/login', '/404', '/401']

/**
 * 是否已添加动态路由的标记
 */
let dynamicRoutesAdded = false

/**
 * 全局前置守卫
 * 负责：
 * 1. 登录状态检查
 * 2. 动态路由生成和注册
 * 3. 页面标题设置
 */
router.beforeEach(async (to, from, next) => {
  console.log('[Router Guard] 导航:', from.path, '->', to.path, '已生成路由:', dynamicRoutesAdded)
  // 显示进度条
  NProgress.start()

  // 设置页面标题
  const { t } = i18n.global
  const systemTitle = t('system.title')
  const menuTitle = to.meta.title ? t(`menu.${to.meta.title}`, to.meta.title) : ''
  document.title = menuTitle ? `${menuTitle} - ${systemTitle}` : systemTitle

  const userStore = useUserStore()
  const permissionStore = usePermissionStore()

  // 1. 白名单路由直接放行
  if (whiteList.includes(to.path)) {
    next()
    return
  }

  // 2. 检查是否有Token
  const hasToken = userStore.token
  if (!hasToken) {
    // 没有token，跳转登录页
    next({ path: '/login', query: { redirect: to.fullPath } })
    NProgress.done()
    return
  }

  // 3. 已登录用户访问登录页，重定向到首页
  if (to.path === '/login') {
    next({ path: '/' })
    NProgress.done()
    return
  }

  // 4. 检查是否已获取用户信息并生成路由
  const hasRoles = userStore.roles && userStore.roles.length > 0
  const hasRoutes = permissionStore.isRoutesGenerated && dynamicRoutesAdded

  if (hasRoles && hasRoutes) {
    // 已有用户信息和路由
    // 检查路由是否存在（处理直接输入URL的情况）
    if (to.matched.length === 0) {
      // 路由不匹配，跳转404
      next('/404')
      return
    }

    // 路由已存在，直接放行
    // 使用 next() 而不是 next({ ...to, replace: true }) 避免不必要的重新渲染
    next()
    return
  }

  // 5. 首次进入，需要获取用户信息并生成路由
  try {
    // 获取用户信息
    if (!hasRoles) {
      await userStore.getInfo()
    }

    // 生成动态路由
    console.log('[Router] 开始生成动态路由...')
    const accessRoutes = await permissionStore.generateRoutes(userStore.permissions)
    console.log('[Router] 生成的路由:', accessRoutes.length, accessRoutes.map(r => ({ path: r.path, name: r.name, children: r.children?.length || 0 })))

    // 动态添加路由
    accessRoutes.forEach(route => {
      router.addRoute(route)
      console.log('[Router] 添加路由:', route.path, route.name)
      // 打印子路由信息
      if (route.children && route.children.length > 0) {
        console.log('[Router]   子路由:', route.children.map(c => ({ path: c.path, name: c.name, component: c.component?.name || 'async' })))
      }
    })

    // 添加404通配路由（必须在最后添加）
    router.addRoute({
      path: '/:pathMatch(.*)*',
      redirect: '/404',
      meta: { hidden: true }
    })

    // 标记动态路由已添加
    dynamicRoutesAdded = true

    console.log('[Router] 动态路由注册完成，数量:', accessRoutes.length)
    console.log('[Router] permissionStore.routes:', permissionStore.routes.length)

    // 动态路由注册完成
    // 如果目标路由已匹配静态路由（如 /dashboard），无需重新导航，直接放行
    // 如果目标路由之前不匹配（如刷新页面在动态路由上），需要重新导航让 Router 重新解析
    if (to.matched.length > 0) {
      next()
    } else {
      next({ ...to, replace: true })
    }

  } catch (error) {
    console.error('[Router] 路由生成失败:', error)

    // 清除token并跳转登录页
    await userStore.resetToken()
    permissionStore.resetPermission()
    dynamicRoutesAdded = false

    next({ path: '/login', query: { redirect: to.fullPath } })
    NProgress.done()
  }
})

/**
 * 全局后置钩子
 */
router.afterEach((to, from) => {
  // 结束进度条
  NProgress.done()
})

/**
 * 路由错误处理
 * 注意：不再自动调用 window.location.reload()，避免整页刷新导致状态丢失
 */
router.onError((error) => {
  console.error('[Router] 路由错误:', error)
  NProgress.done()
  // 仅记录日志，不自动刷新页面。用户可手动刷新浏览器来恢复。
})

/**
 * 重置路由
 * 用于退出登录时清除动态添加的路由
 */
export function resetRouter() {
  const permissionStore = usePermissionStore()

  // 移除所有动态添加的路由
  permissionStore.routes.forEach(route => {
    if (route.name && router.hasRoute(route.name)) {
      router.removeRoute(route.name)
    }
  })

  // 重置标记
  dynamicRoutesAdded = false

  console.debug('[Router] 路由已重置')
}

export default router
